import { Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import {
  Dialog,
  DialogProps,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { DialogSuccessContent } from "../../../../components/DialogSuccessContent";
import { DialogErrorContent } from "../../../../components/DialogErrorContent";
import { useTranslation } from "react-i18next";
import { UpdateEmailTemplateDrawerContent } from "./contents/UpdateEmailTemplateDrawerContent";
import { RouteSearchParam } from "../../../../routes/RoutesHelper";
import { useSearchParams } from "react-router-dom";
import { Mode } from "../../../enums/Mode";
import { HelpCircle } from "lucide-react";
import { observer } from "mobx-react";
import {
  useEmailTemplatesStore,
  useUpdateEmailTemplateStore,
} from "../../stores/Hooks";
import { NonDismissibleDrawer } from "../../../../components/drawer/NonDismissibleDrawer";

interface UpdateEmailTemplateDrawerProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isFromEmailDetailsDrawer: boolean;
}

interface DialogType extends DialogProps {
  description: string;
  isError?: boolean;
}

const UPDATE_EMAIL_TEMPLATE_DRAWER_WIDTH = "1024px";
const UNSAVED_CHANGES_DIALOG_DESCRIPTION_ICON_SIZE = 15;

export const UpdateEmailTemplateDrawer: React.FC<UpdateEmailTemplateDrawerProps> =
  observer(({ open, setOpen, isFromEmailDetailsDrawer }) => {
    const [openUnsavedChangesDialog, setOpenUnsavedChangesDialog] =
      useState(false);
    const [openSuccessMessageDialog, setOpenSuccessMessageDialog] =
      useState(false);
    const [
      openProjectArchivedErrorMessageDialog,
      setOpenProjectArchivedErrorMessageDialog,
    ] = useState(false);
    const [openErrorMessageDialog, setOpenErrorMessageDialog] = useState(false);
    const [, setSearchParams] = useSearchParams();

    const { t } = useTranslation();
    const tokens = useFoundationColorTokens();
    const typography = useTypography();
    const spacing = useSpacing();
    const emailTemplatesStore = useEmailTemplatesStore();
    const updateEmailTemplateStore = useUpdateEmailTemplateStore();

    const messageDialogs: DialogType[] = [
      // Successful message dialog
      {
        open: openSuccessMessageDialog,
        title: t("emailTemplates.updateSuccessfulDialogTitle"),
        onSecondaryButtonClick: (): void => {
          setOpenSuccessMessageDialog(false);
          emailTemplatesStore.getEmailTemplates();
          setSearchParams((prev) => {
            prev.set(RouteSearchParam.Mode, Mode.View);
            return prev;
          });
        },
        description: t("emailTemplates.updateSuccessfulDialogDescription"),
      },
      // Project archived error message dialog
      {
        open: openProjectArchivedErrorMessageDialog,
        title: t("common.projectAlreadyArchived"),
        onSecondaryButtonClick: (): void => {
          emailTemplatesStore.getEmailTemplates();
          setOpenProjectArchivedErrorMessageDialog(false);
        },
        description: t("common.projectAlreadyArchivedErrorText"),
        isError: true,
      },
      // Unexpected error message dialog
      {
        open: openErrorMessageDialog,
        title: t("emailTemplates.updateErrorDialogTitle"),
        onSecondaryButtonClick: (): void => {
          setOpenErrorMessageDialog(false);
          setSearchParams((prev) => {
            prev.delete(RouteSearchParam.TemplateId);
            prev.delete(RouteSearchParam.Mode);
            return prev;
          });
        },
        description: t("emailTemplates.updateErrorDialogDescription"),
        isError: true,
      },
    ];

    const handleDrawerClose = (): void =>
      setSearchParams((prev) => {
        if (isFromEmailDetailsDrawer) {
          prev.set(RouteSearchParam.Mode, Mode.View);
        } else {
          prev.delete(RouteSearchParam.TemplateId);
          prev.delete(RouteSearchParam.Mode);
        }
        return prev;
      });

    return (
      <>
        <NonDismissibleDrawer
          open={open}
          onClose={() =>
            !updateEmailTemplateStore.disableSubmit
              ? setOpenUnsavedChangesDialog(true)
              : handleDrawerClose()
          }
          anchor="right"
          slotProps={{
            paper: {
              sx: {
                width: "100%",
                maxWidth: UPDATE_EMAIL_TEMPLATE_DRAWER_WIDTH,
              },
            },
          }}
        >
          <UpdateEmailTemplateDrawerContent
            handleDrawerClose={handleDrawerClose}
            setDrawerOpen={setOpen}
            setOpenUnsavedChangesDialog={setOpenUnsavedChangesDialog}
            setOpenSuccessMessageDialog={setOpenSuccessMessageDialog}
            setOpenErrorMessageDialog={setOpenErrorMessageDialog}
            setOpenProjectArchivedErrorMessageDialog={
              setOpenProjectArchivedErrorMessageDialog
            }
          />
        </NonDismissibleDrawer>

        {/* Unsaved changes dialog */}
        <Dialog
          open={openUnsavedChangesDialog}
          title={t("common.unsavedChanges")}
          primaryButtonText={t("common.confirm")}
          onPrimaryButtonClick={async () => {
            setOpenUnsavedChangesDialog(false);
            handleDrawerClose();
          }}
          secondaryButtonText={t("common.cancel")}
          onSecondaryButtonClick={() => setOpenUnsavedChangesDialog(false)}
          disableBackdropClick={true}
        >
          <Stack direction="row" alignItems="center" gap={spacing.spaceXS}>
            <HelpCircle size={UNSAVED_CHANGES_DIALOG_DESCRIPTION_ICON_SIZE} />
            <Typography {...typography.b2}>
              {t("emailTemplates.unsavedChangesDialogDescription")}
            </Typography>
          </Stack>
        </Dialog>

        {messageDialogs.map(
          ({ description, isError, ...dialogProps }, index) => (
            <Dialog
              key={index}
              secondaryButtonText={t("common.done")}
              {...dialogProps}
              disableBackdropClick={true}
            >
              {isError ? (
                <DialogErrorContent
                  spacing={spacing}
                  typography={typography}
                  tokens={tokens}
                  errorText={description}
                />
              ) : (
                <DialogSuccessContent
                  spacing={spacing}
                  typography={typography}
                  tokens={tokens}
                  description={description}
                />
              )}
            </Dialog>
          ),
        )}
      </>
    );
  });
